import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { CollectionKey } from "../../../netlify/functions/_shared/types";
import {
  getEventMeta,
  getAllArtists,
  getAllDays,
  getAllStages,
  getAllPerformances,
  reloadFestivalData,
} from "../../services/dataService";
import {
  adminCreateEntity,
  adminUpdateEntity,
  adminUpdateCollection,
} from "../../services/adminDataService";
import { entitySchemas } from "./editorSchema";
import { EntityForm } from "./EntityForm";

export const EntityEditorPage = () => {
  const { collection, id } = useParams<{ collection: string; id: string }>();
  const navigate = useNavigate();
  const [jsonText, setJsonText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editorMode, setEditorMode] = useState<"form" | "json">("form");

  const schema = entitySchemas[collection as keyof typeof entitySchemas];

  useEffect(() => {
    try {
      if (collection === "festivalInfo") {
        setJsonText(JSON.stringify(getEventMeta(), null, 2));
        setEditorMode("json");
        return;
      }

      if (id === "new") {
        let template: Record<string, unknown> = { id: "" };
        if (collection === "artists") {
          template = {
            id: "",
            name: "",
            collective: null,
            description: "",
            genre: null,
            image: null,
          };
        } else if (collection === "days") {
          template = {
            id: "",
            date: "",
            name: "",
            description: "",
            facebookEvent: "",
            themeColors: {
              primary: "",
              secondary: "",
              background: "",
              text: "",
            },
          };
        } else if (collection === "stages") {
          template = {
            id: "",
            name: "",
            description: "",
            icon: "",
            image: null,
            offsetX: 0,
            offsetY: 0,
            hidden: false,
          };
        } else if (collection === "performances") {
          template = {
            id: "",
            artistId: "",
            dayId: "",
            description: "",
            stageId: "",
            startTime: "",
            endTime: "",
          };
        }
        setJsonText(JSON.stringify(template, null, 2));
        setEditorMode("form");
        return;
      }

      let item: unknown = undefined;
      if (collection === "artists") item = getAllArtists().find((i) => i.id === id);
      else if (collection === "days") item = getAllDays().find((i) => i.id === id);
      else if (collection === "stages") item = getAllStages().find((i) => i.id === id);
      else if (collection === "performances") item = getAllPerformances().find((i) => i.id === id);

      if (item) {
        setJsonText(JSON.stringify(item, null, 2));
        setEditorMode("form");
      } else {
        setError("Item not found");
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [collection, id]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const parsed = JSON.parse(jsonText);

      if (collection === "festivalInfo") {
        await adminUpdateCollection(collection as CollectionKey, parsed);
        await reloadFestivalData(true);
        alert("Saved festival info");
        navigate("/admin");
        return;
      }

      if (id === "new") {
        await adminCreateEntity(collection as CollectionKey, parsed);
      } else {
        await adminUpdateEntity(collection as CollectionKey, id as string, parsed);
      }
      await reloadFestivalData(true);
      navigate(`/admin/${collection}`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSwitchToForm = () => {
    try {
      JSON.parse(jsonText || "{}");
      setEditorMode("form");
      setError(null);
    } catch (e: unknown) {
      setError(
        `Cannot switch to Form Editor because the current raw JSON has syntax errors: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <div className="flex justify-between items-center border-b border-border pb-3">
        <h1 className="text-2xl font-bold font-['Unbounded'] capitalize text-white">
          {id === "new" ? `New ${collection}` : `Edit ${collection}`}
        </h1>
        <div className="space-x-2">
          <button
            type="button"
            onClick={() => navigate(`/admin/${collection === "festivalInfo" ? "" : collection}`)}
            className="px-4 py-2 bg-background-accent hover:bg-card-hover border border-border text-text rounded-lg transition duration-150 font-medium text-sm cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-main hover:opacity-90 disabled:opacity-50 text-background rounded-lg font-bold transition duration-150 text-sm shadow-sm cursor-pointer"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {schema && (
        <div className="flex border-b border-border space-x-1">
          <button
            type="button"
            onClick={handleSwitchToForm}
            className={`px-4 py-2 font-semibold text-sm border-b-2 transition-all duration-150 ${
              editorMode === "form"
                ? "border-main text-main font-bold"
                : "border-transparent text-muted hover:text-text cursor-pointer"
            }`}
          >
            📝 Form Editor
          </button>
          <button
            type="button"
            onClick={() => setEditorMode("json")}
            className={`px-4 py-2 font-semibold text-sm border-b-2 transition-all duration-150 ${
              editorMode === "json"
                ? "border-main text-main font-bold"
                : "border-transparent text-muted hover:text-text cursor-pointer"
            }`}
          >
            💻 Raw JSON
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-950/30 border border-red-900/50 text-red-400 p-4 rounded-lg font-mono text-sm">
          {error}
        </div>
      )}

      <div className="min-h-[500px]">
        {schema && editorMode === "form" ? (
          (() => {
            let parsed = {};
            try {
              parsed = JSON.parse(jsonText || "{}");
            } catch (_) {}
            return (
              <EntityForm
                data={parsed}
                schema={schema}
                onChange={(updated) => {
                  setJsonText(JSON.stringify(updated, null, 2));
                }}
              />
            );
          })()
        ) : (
          <div className="grid grid-cols-1 gap-4 h-[600px]">
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              className="w-full h-full p-4 font-mono text-sm border border-border rounded-xl bg-card text-text shadow-sm resize-none focus:outline-none focus:ring-2 focus:ring-main focus:border-main transition duration-150"
              spellCheck={false}
              placeholder="Paste JSON here..."
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EntityEditorPage;
